import React, { useState, useMemo, useCallback, memo } from "react";
import { formatMonthLabel } from "../engines/dateUtils";
import NumInput from "../components/NumInput";
import Spark from "../components/Spark";
import { ERR_NO_IS_LINE } from "../utils/vendorForecast";
// 2026-10-29 (the vendor-forecast file): the row rules and the type / seat-basis lists moved to utils, unchanged, so the Step-6
// file upload builds its vendor objects through this very code (utils/forecastWizardRows.js, utils/vendorForecastTypes.js).
import { vendorContext, prefillConstantAmount, isDone, buildWizardRow, buildVendorObj as buildVendorObjFor } from "../utils/forecastWizardRows";
import { VENDOR_TYPES, SEAT_BASIS_OPTIONS } from "../utils/vendorForecastTypes";
import { useUnsavedChangesWarning } from "../utils/useUnsavedChangesWarning";
import GuardedDialog from "../components/GuardedDialog";   // Batch 1b (L3641): the click-outside / Escape guard
// W-073 (b): the ONE sentinel for "Total Revenue", now STORED as the answer (it used to be converted to NULL).
// 2026-10-27 (B1): its label + help text ("the Income Statement's Total Revenue") from the same owner.
import { PCT_REVENUE_TOTAL as TOTAL_REV, PCT_REVENUE_TOTAL_LABEL as TOTAL_REV_LABEL, PCT_REVENUE_TOTAL_HELP as TOTAL_REV_HELP, PCT_RATE_MESSAGES } from "../utils/pctRevenueSource";
// B5 (Brian, 2026-10-27): the "% of Revenue" rate is typed 0-100 with up to two decimals and stored as a fraction -- the ONE rule the Vendor
// Modal uses (utils/percentText.js). The cell used to store r2(typed / 100), so a typed 2.95% was saved as 0.03 (3%).
import { parsePercentText } from "../utils/percentText";
import ViewOnly, { ViewOnlyNote } from "../components/ViewOnly";

// ════════════════════════════════════════════════════════════════════════════
// cat-1 Phase 2a — Bulk Forecast Wizard (READ + EDIT only; NO save/persistence).
// One grid to set every rule-based vendor's forecast at once instead of opening
// the per-vendor modal 181×. Type-specific inputs (constant $ / pct_revenue
// %+source / per_seat $/seat+basis+depts / one_off → portal note), 3-mo-avg
// prefill for constant, click-to-expand full historical actuals. The actual
// write lands in Phase 2b — here the grid holds local edit state only, so this
// commit cannot touch live data.
// ════════════════════════════════════════════════════════════════════════════

const PAY_FREQ = [
  { id: "monthly", label: "Monthly", months: 1 },
  { id: "quarterly", label: "Quarterly", months: 3 },
  { id: "semi_annual", label: "Semi-Annual", months: 6 },
  { id: "annual", label: "Annual", months: 12 },
];
// (VENDOR_TYPES and SEAT_BASIS_OPTIONS are imported above -- utils/vendorForecastTypes.js.)
// ★ Item 7 — FIXED (the ⚠ GO-LIVE warning that stood here is resolved; it read:
// "these seat-department keys are Semitek-shaped and do NOT match cid=3 … per_seat-
// WITH-departments therefore mis-buckets headcount … must be made tenant-dynamic
// before go-live"). The hardcoded Semitek list it warned about is gone:
// the option list now arrives as the `seatDeptOptions` prop, built by the ONE shared
// resolver (utils/seatDepartments.js) from the tenant's department_mappings. The
// Semitek six survive only as that resolver's LEGACY branch (cid=1/cid=2, which have
// no department_mappings rows and whose payrollHC keys are the old literals).

/* ⛔ NOT A MONTH-LABEL COPY — DO NOT FOLD THIS INTO formatMonthLabel.
   It is indexed by a 1-12 MONTH NUMBER from the anchor-month <select> below, not by
   a "YYYY-MM", so the canonical formatter cannot take its input. Same class as
   PayrollPage's month-of-year picker. It survives the consolidation deliberately. */
const MONTHS_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const r2 = n => Math.round(n * 100) / 100;
const fmt = n => !n || n === 0 ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(r2(n));
const fmtFull = n => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const freqMonths = id => (PAY_FREQ.find(f => f.id === id)?.months) || 1;
const AMOUNT_LABEL = { monthly: "Monthly Amount", quarterly: "Quarterly Amount", semi_annual: "Semi-Annual Amount", annual: "Annual Amount" };
// (TOTAL_REV is imported above -- W-073 (b).)

const RULES = [
  { id: "three_mo_avg", label: "3 Mo Avg" },
  { id: "cutoff_value", label: "Cutoff-Month Value" },
  { id: "zero", label: "All $0" },
];

// (vendorContext, prefillConstantAmount, isDone and firstNonzeroForecast are imported above -- utils/forecastWizardRows.js.)

const cellInput = { width: "100%", padding: "6px 8px", background: "#F7F8FA", border: "1px solid rgba(0,0,0,0.1)", borderRadius: "6px", color: "#1A1C2E", fontSize: "12px", fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" };
const amtInput = { ...cellInput, textAlign: "right", fontFamily: "'Space Mono',monospace" };
const capStyle = { fontSize: "9px", fontWeight: 600, color: "#8B8FA3", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "2px" };

// ── per_seat departments: multi-select checkbox dropdown ──
// State is simply seatDepartments = [] (company-wide) OR [dept,…] (specific) —
// no separate company-wide flag; empty array IS company-wide (engine reads it
// that way: [] → total headcount, [depts] → bucketed). String[] shape unchanged.
function SeatDeptDropdown({ value, onChange, SEAT_DEPARTMENTS }) {
  const [open, setOpen] = useState(false);
  const depts = value || [];
  const companyWide = depts.length === 0;
  const labelOf = id => SEAT_DEPARTMENTS.find(d => d.id === id)?.label || id;
  const trigger = companyWide ? "Company-wide"
    : depts.length === 1 ? labelOf(depts[0])
      : `${labelOf(depts[0])} +${depts.length - 1}`;
  const toggleDept = id => onChange(depts.includes(id) ? depts.filter(d => d !== id) : [...depts, id]);
  return (
    <div style={{ position: "relative", marginTop: "4px" }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{ ...cellInput, textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{trigger}</span>
        <span style={{ color: "#8B8FA3", marginLeft: "6px" }}>▾</span>
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 1099 }} onClick={e => { e.stopPropagation(); setOpen(false); }} />
          <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "100%", left: 0, zIndex: 1100, marginTop: "2px", background: "#fff", border: "1px solid rgba(0,0,0,0.12)", borderRadius: "8px", boxShadow: "0 12px 40px rgba(0,0,0,0.18)", padding: "6px", width: "210px", maxHeight: "240px", overflowY: "auto" }}>
            {/* Company-wide = the empty state; checking it clears all depts. */}
            <label style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 6px", fontSize: "12px", cursor: "pointer", borderBottom: "1px solid rgba(0,0,0,0.06)", fontWeight: 600 }}>
              <input type="checkbox" checked={companyWide} onChange={() => onChange([])} style={{ accentColor: "#2E86C1" }} />
              Company-wide
            </label>
            {SEAT_DEPARTMENTS.map(d => (
              <label key={d.id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 6px", fontSize: "12px", cursor: "pointer" }}>
                <input type="checkbox" checked={depts.includes(d.id)} onChange={() => toggleDept(d.id)} style={{ accentColor: "#2E86C1" }} />
                {d.label}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Type-specific forecast input cell ──
function ForecastCell({ vendor, row, onChange, done, confirmed, seatDeptOptions }) {
  if (row.type === "pct_revenue") {
    return (
      <>
        <div style={{ ...capStyle, textAlign: "right" }}>% of Revenue</div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {/* B5: a text draft, parsed by utils/percentText.js -- 9 -> 0.09, 2.95 -> 0.0295 (it was r2(typed / 100): 2.95 -> 0.03). A
              refused text keeps the last stored rate and is named below; the save refuses while it stands. */}
          <input type="text" inputMode="decimal" style={amtInput} value={row.pctText || ""} placeholder="0-100"
            onChange={e => { const t = e.target.value; const pr = parsePercentText(t, PCT_RATE_MESSAGES); onChange(vendor.id, pr.error ? { pctText: t } : { pctText: t, pctRate: pr.rate }); }} />
          <span style={{ fontSize: "12px", color: "#6B7094" }}>%</span>
        </div>
        {parsePercentText(row.pctText, PCT_RATE_MESSAGES).error &&
          <div data-pct-rate-error="1" style={{ fontSize: "10px", color: "#C0392B", fontWeight: 600, marginTop: "2px" }}>{parsePercentText(row.pctText, PCT_RATE_MESSAGES).error}</div>}
        <select style={{ ...cellInput, marginTop: "4px" }} value={row.pctRevenueSource || ""}
          onChange={e => onChange(vendor.id, { pctRevenueSource: e.target.value || null })}>
          {/* W-073 (b): no default -- blank and required until answered; "Total Revenue" is stored as the sentinel. */}
          <option value="" disabled>Required: choose a revenue base</option>
          <option value={TOTAL_REV} title={TOTAL_REV_HELP}>{TOTAL_REV_LABEL}</option>
          {(row.sourceOptions || []).map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </>
    );
  }
  if (row.type === "per_seat") {
    return (
      <>
        <div style={{ ...capStyle, textAlign: "right" }}>$ per Seat</div>
        <NumInput value={row.perSeatCost} decimals={2} style={amtInput}
          onChange={v => onChange(vendor.id, { perSeatCost: v })} />
        <select style={{ ...cellInput, marginTop: "4px" }} value={row.seatBasis}
          onChange={e => onChange(vendor.id, { seatBasis: e.target.value })}>
          {SEAT_BASIS_OPTIONS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
        <SeatDeptDropdown value={row.seatDepartments} onChange={dd => onChange(vendor.id, { seatDepartments: dd })} SEAT_DEPARTMENTS={seatDeptOptions || []} />
        <div style={{ fontSize: "9px", color: "#B0B3C8", marginTop: "2px" }}>seats from payroll/contractor HC · company-wide if none</div>
      </>
    );
  }
  if (row.type === "one_off") {
    return (
      <>
        <div style={{ ...capStyle, textAlign: "right" }}>—</div>
        <div style={{ fontSize: "11px", color: "#8B7400", background: "rgba(212,172,13,0.08)", border: "1px solid rgba(212,172,13,0.2)", borderRadius: "6px", padding: "6px 8px" }}>
          Set per-month amounts in the Vendor portal.
        </div>
      </>
    );
  }
  // constant
  const nonMonthly = row.freq !== "monthly";
  return (
    <>
      <div style={{ ...capStyle, textAlign: "right" }}>{AMOUNT_LABEL[row.freq] || "Amount"}</div>
      {/* fix #3 showZero={done || confirmed}: a saved $0 reads "$0" (done); a freshly-
          typed 0 stays "0" after blur (editing auto-confirms the row); an untouched
          todo $0 prefill stays blank. fix #4 decimals={2}: dollars and cents. */}
      <NumInput value={row.amount} decimals={2} style={amtInput} showZero={done || confirmed} onChange={v => onChange(vendor.id, { amount: v })} />
      {nonMonthly && (
        <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#8B8FA3" }}>
          <span>books each</span>
          <select style={{ ...cellInput, width: "auto", padding: "2px 4px", fontSize: "10px" }} value={row.anchorMM}
            onChange={e => onChange(vendor.id, { anchorMM: Number(e.target.value) })}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(mm => <option key={mm} value={mm}>{MONTHS_ABBR[mm - 1]}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

// ── One memoized row: re-renders only when its own slice / ctx changes. ──
const WizardRow = memo(function WizardRow({ vendor, ctx, row, onChange, forecastMonths, actualsMonths, isLabel, confirmed, onToggleConfirm, done, seatDeptOptions, hasSourceOptions }) {
  const [pop, setPop] = useState(false);
  // full UNROUNDED trailing-12 actuals for the click-to-expand popover.
  const popData = useMemo(() => actualsMonths.slice(-12).map(m => ({ m, v: (vendor.monthlyData || {})[m] || 0 })), [actualsMonths, vendor]);
  const isConstant = row.type === "constant";

  return (
    <tr style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", background: confirmed ? "rgba(39,174,96,0.04)" : "transparent" }}>
      {/* Confirm — Save writes only checked rows; any edit auto-checks. */}
      <td style={{ padding: "8px 10px", textAlign: "center", width: "34px" }}>
        <input type="checkbox" checked={!!confirmed} onChange={() => onToggleConfirm(vendor.id)} style={{ width: "14px", height: "14px", accentColor: "#27AE60", cursor: "pointer" }} />
      </td>
      <td style={{ padding: "8px 10px", maxWidth: "170px" }}>
        <div style={{ fontWeight: 600, color: "#1A1C2E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{vendor.name}</div>
        {/* done/todo status — distinct from the Confirm checkbox (= save this session). */}
        <span style={{ display: "inline-block", marginTop: "2px", fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", padding: "1px 6px", borderRadius: "6px",
          background: done ? "rgba(39,174,96,0.12)" : "rgba(139,143,163,0.1)", color: done ? "#1E8449" : "#8B8FA3" }}>
          {done ? "● Set" : "○ Todo"}
        </span>
      </td>
      <td style={{ padding: "8px 10px" }}>
        <span style={{ fontSize: "11px", color: "#6B7094" }}>{isLabel || "—"}</span>
        <div style={{ fontFamily: "'Space Mono',monospace", fontSize: "9px", color: "#B0B3C8" }}>{vendor.isCat}</div>
      </td>
      {/* History — click to expand full unrounded actuals */}
      <td style={{ padding: "8px 10px", position: "relative", cursor: "pointer" }} onClick={() => setPop(p => !p)} title="Click for full monthly history">
        <Spark data={ctx.series} w={96} h={26} color="#2A6DB0" />
        <div style={{ fontSize: "10px", color: "#8B8FA3" }}>3-mo avg {fmt(ctx.avg3)}{!ctx.active12 && <span style={{ color: "#C0392B", marginLeft: "4px" }}>· no spend 12mo</span>}</div>
        {pop && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 1099 }} onClick={e => { e.stopPropagation(); setPop(false); }} />
            <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "100%", left: "8px", zIndex: 1100, marginTop: "4px", background: "#fff", border: "1px solid rgba(0,0,0,0.12)", borderRadius: "10px", boxShadow: "0 12px 40px rgba(0,0,0,0.18)", padding: "10px 12px", width: "220px", maxHeight: "320px", overflowY: "auto" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#6B7094", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "6px" }}>Historical actuals — trailing 12</div>
              {popData.map(({ m, v }) => (
                <div key={m} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", padding: "2px 0", borderBottom: "1px solid rgba(0,0,0,0.03)" }}>
                  <span style={{ color: "#6B7094" }}>{formatMonthLabel(m, "shortYear")}</span>
                  <span style={{ fontFamily: "'Space Mono',monospace", color: v ? "#1A1C2E" : "#B0B3C8" }}>{fmtFull(v)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </td>
      <td style={{ padding: "8px 10px" }}>
        {/* ★ 4b: '% of Revenue' is UNSELECTABLE while no revenue source lines exist.
            Picking it there would store a NULL source, which books rate × TOTAL
            REVENUE. Data-driven rather than host-driven, so the Vendor Dashboard
            (which has options) is unaffected — and a Dashboard tenant that somehow
            had none would be protected too. A row that ALREADY is pct_revenue stays
            selectable so it can be read and re-typed, never silently re-written. */}
        <select style={cellInput} value={row.type} onChange={e => onChange(vendor.id, { type: e.target.value })}>
          {VENDOR_TYPES.map(t => {
            const blocked = t.id === "pct_revenue" && !hasSourceOptions && row.type !== "pct_revenue";
            return (
              <option key={t.id} value={t.id} disabled={blocked}
                title={blocked ? "Available after Step 7 (revenue modules) — set rate + source together on the Vendor Dashboard" : undefined}>
                {t.label}{blocked ? " — after Step 7" : ""}
              </option>
            );
          })}
        </select>
      </td>
      {/* Frequency — constant only; hidden (—) for pct/per_seat/one_off */}
      <td style={{ padding: "8px 10px" }}>
        {isConstant ? (
          <select style={cellInput} value={row.freq} onChange={e => {
            const nf = e.target.value;
            const patch = { freq: nf };
            // D8 guard: a unit change CLEARS the amount (no silent ×12 — prefill is monthly).
            if (freqMonths(nf) !== freqMonths(row.freq)) patch.amount = 0;
            onChange(vendor.id, patch);
          }}>
            {PAY_FREQ.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        ) : <span style={{ fontSize: "12px", color: "#B0B3C8" }}>—</span>}
      </td>
      <td style={{ padding: "8px 10px", minWidth: "96px" }}>
        <select style={{ ...cellInput, minWidth: "84px" }} value={row.startDate} onChange={e => onChange(vendor.id, { startDate: e.target.value })}>
          {forecastMonths.map(m => <option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
        </select>
      </td>
      <td style={{ padding: "8px 10px", minWidth: "136px" }}>
        <select style={{ ...cellInput, minWidth: "124px" }} value={row.endDate} onChange={e => onChange(vendor.id, { endDate: e.target.value })}>
          <option value="until_canceled">Until Canceled</option>
          {forecastMonths.map(m => <option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
        </select>
      </td>
      <td style={{ padding: "8px 10px", minWidth: "170px" }}>
        <ForecastCell vendor={vendor} row={row} onChange={onChange} done={done} confirmed={confirmed} seatDeptOptions={seatDeptOptions} />
      </td>
    </tr>
  );
});

/* ★★ C3 — `saveMode` IS THE HOST'S DECLARATION OF WHAT ITS `onSaveVendors` DOES.
 *
 * THE DEFECT THIS CLOSES: this component has TWO hosts that mean OPPOSITE things by
 * "Save", and it could not tell them apart.
 *   · VendorDashboard (saveWizardVendors) PERSISTS — chunked persistVendor writes,
 *     then markStale(). "Saved 12 vendors" is TRUE there.
 *   · AdminPage Step 6 (step6SaveForecastRows) DOES NOT — it is a synchronous
 *     setStep6Vendors fold with no api call. The real write is the separate
 *     "Save Progress" button, which the modal covers.
 * The component nonetheless rendered a green "Save 12", claimed "Saved 12 vendors",
 * and CLEARED the dirty set — which unregistered the beforeunload guard (the hook
 * only listens while dirty), so after a memory-only "save" the operator could close
 * the tab and lose everything, unwarned. The save did not merely fail to persist:
 * it removed the protection that would have caught it.
 *
 * ★ THE SEMANTICS TRAVEL AS A PROP, NEVER AS A HARDCODE. Baking either meaning in
 * would break the other host, and the shared useUnsavedChangesWarning hook (4
 * consumers) is deliberately untouched — the caller owns `isDirty`, this owns the
 * browser, and C3 only changes what we pass IN.
 *
 * ★ THE DEFAULT IS 'local' ON PURPOSE — FAIL-SAFE, NOT FAIL-CONVENIENT. A future
 * host that forgets to declare gets the honest behaviour: no "Saved" claim, guard
 * stays armed. The failure mode is a nuisance (an over-cautious warning) instead of
 * silent data loss. Defaulting to 'persist' would make forgetting the prop cost the
 * operator their work, which is the bug we are fixing.
 */
/* W-053 3b: canEdit -- may this login change the model? FROM THE HOST (VendorDashboard: useModel().canEditModel; AdminPage Step 6:
   canEditModel(user)). Default false: fail-closed. A false greys the rules, the check controls, the grid and Save (components/ViewOnly);
   Close stays live. */
export default function ForecastWizard({ vendors, forecastMonths, actualsMonths, cutoff, firstForecast, isLineTaxonomy, pctRevenueSourceOptions, onSaveVendors, onClose, seatDeptOptions, saveMode = "local", canEdit = false }) {
  const isLocal = saveMode !== "persist";
  const VERB = isLocal ? "Apply" : "Save";
  const [rule, setRule] = useState("three_mo_avg");
  /* ★ 4b: the ONE derivation behind both halves of the %-of-revenue gate — the type
   * option's disabled state and isDone. Empty list = the operator has no source to
   * choose, so the type cannot be picked and no % row reads as done. */
  const hasSourceOptions = (pctRevenueSourceOptions || []).length > 0;

  const ctxById = useMemo(() => {
    const m = {};
    vendors.forEach(v => { m[v.id] = vendorContext(v, actualsMonths); });
    return m;
  }, [vendors, actualsMonths]);

  // Build grid edit-state. 2b-ii — DONE vendors load their SAVED forecast (don't
  // re-prefill); TODO vendors get the prefill rule. constant config (freq/start/
  // end/anchor) + pct/per_seat config load from the vendor either way.
  const buildRows = (r) => {
    const out = {};
    vendors.forEach(v => {
      out[v.id] = buildWizardRow(v, { ctx: ctxById[v.id], rule: r, cutoff, firstForecast, hasSourceOptions, pctRevenueSourceOptions });
    });
    return out;
  };
  const [rows, setRows] = useState(() => buildRows("three_mo_avg"));

  // 2b-ii: prefill rule re-derives ONLY todo constant rows — DONE rows keep their
  // loaded saved values (so a live tenant can toggle the rule without clobbering
  // already-saved forecasts).
  const applyRule = (r) => {
    setRule(r);
    setRows(prev => {
      const out = { ...prev };
      vendors.forEach(v => {
        if (isDone(v, cutoff, hasSourceOptions)) return;
        if ((v.type || v.forecast_type || "constant") !== "constant") return;
        out[v.id] = { ...out[v.id], amount: prefillConstantAmount(r, ctxById[v.id]) };
      });
      return out;
    });
  };

  // confirmedIds = rows checked to Save this session — DONE rows pre-checked (saved).
  // dirtyIds = rows the operator EDITED this session — drives the unsaved-changes
  // warning (so merely opening a done wizard never warns; editing does).
  const [confirmedIds, setConfirmedIds] = useState(() => new Set(vendors.filter(v => isDone(v, cutoff, (pctRevenueSourceOptions || []).length > 0)).map(v => v.id)));
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState("");
  const [noteIsError, setNoteIsError] = useState(false);   // ★ Item 8b: colours the note red
  /* ★ C3 — THE SECOND GUARD, AND WHY ONE WAS NOT ENOUGH. In local mode there are
   * TWO distinct kinds of unsaved work, lost by two different actions:
   *   (1) edits still in this component's `rows` — lost by CLOSING the wizard;
   *       tracked by dirtyIds, which must still clear on a successful apply or
   *       the close-confirm would fire forever.
   *   (2) applied edits sitting in the parent's step6Vendors — safe from a close,
   *       lost by closing the TAB, and not saved until the Step-6 import runs.
   * (2) is exactly what the old code destroyed by clearing dirtyIds, so it needs
   * its own latch. Set once on a local apply and never cleared for the wizard's
   * lifetime: the DB write happens on another screen, so this component can never
   * observe it and must not pretend to. */
  const [localApplied, setLocalApplied] = useState(false);

  // Patch a row immutably (memoized rows skip re-render), auto-confirm + mark dirty.
  const onRowChange = useCallback((id, patch) => {
    setRows(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    setConfirmedIds(prev => prev.has(id) ? prev : new Set(prev).add(id));
    setDirtyIds(prev => prev.has(id) ? prev : new Set(prev).add(id));
  }, []);
  const toggleConfirm = useCallback((id) => {
    setConfirmedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  // check-all / check-all-todo / clear (bulk confirm for a full save).
  const checkAll = () => setConfirmedIds(new Set(vendors.map(v => v.id)));
  const checkAllTodo = () => setConfirmedIds(prev => { const n = new Set(prev); vendors.forEach(v => { if (!isDone(v, cutoff, hasSourceOptions)) n.add(v.id); }); return n; });
  const clearChecks = () => setConfirmedIds(new Set());

  // progress + dirty (recompute from the LIVE vendors prop — updates after Save).
  const doneCount = useMemo(() => vendors.filter(v => isDone(v, cutoff, hasSourceOptions)).length, [vendors, cutoff, hasSourceOptions]);
  const dirty = dirtyIds.size > 0;
  /* ★ C3 — `unsaved` = "something on this screen is not in the database yet", which
   * is the question every indicator and the browser guard should actually be asking.
   * In persist mode it is just `dirty` (unchanged). In local mode a successful apply
   * moves work from (1) to (2) above — still not saved — so the indicators must stay
   * on rather than going quiet, which is what made the old behaviour a lie. */
  const unsaved = dirty || (isLocal && localApplied);

  // 5b-safety: warn on browser close / navigate with unsaved edits.
  // ★ Shared effect — utils/useUnsavedChangesWarning (was one of three copies).
  // ★ C3: the HOOK IS UNTOUCHED — only the value handed to it changed, so the other
  // three consumers are unaffected and the guard stays armed after a local apply.
  useUnsavedChangesWarning(unsaved);

  /* ★ C3 (C) — ONE CLOSE PATH FOR THE BUTTON AND THE BACKDROP. They used to disagree:
   * the backdrop silently ignored the click when dirty (:487) while the Close button
   * discarded unconditionally (:495) — and the footer copy described the guarded one
   * ("click outside won't close"), so the message advertised a protection sitting
   * next to a control that did not provide it.
   * ★ IT KEYS ON `dirty`, NOT `unsaved`: closing loses only the edits that have not
   * reached the host. In local mode, applied edits genuinely survive in the parent's
   * step6Vendors and feed Save Progress, so closing is the intended next step and
   * must not nag — the tab-level guard above is what covers those. A confirm rather
   * than the backdrop's dead-click, because a button that ignores you reads broken. */
  /* Batch 1b (2026-11-03, L3641): the question is asked IN THE PAGE (never a native confirm, which blocks the browser): Close with
   * edits not yet applied / saved shows "Close and discard them?" with Keep editing / Discard and close. The backdrop and Escape are
   * GuardedDialog's: with such edits they do nothing (the other guarded dialogs' rule). */
  const [closeAsk, setCloseAsk] = useState(false);
  const requestClose = () => {
    if (dirty) { setCloseAsk(true); return; }
    onClose();
  };

  // Build the persist-ready vendor object for a checked row: spread the ORIGINAL
  // vendor (preserves notes/active/contractor/etc. that save()'s dbPayload needs)
  // + apply the row's edited config. Non-monthly constant carries the resolved
  // billing anchor (YYYY-MM) so synthesizeForecastVmas books on the right cadence.
  const buildVendorObj = (vendor, row) => buildVendorObjFor(vendor, row, forecastMonths);

  const doSave = async () => {
    const ids = [...confirmedIds];
    if (!ids.length || saving) return;
    /* B5: a checked % of Revenue row whose rate text is refused (blank / out of range / a third decimal) blocks the save, ALL-OR-NOTHING
     * like the unrouted-vendor refusal below -- named, nothing sent; each row's own message stands under its cell. */
    const badPct = ids.filter(id => rows[id] && rows[id].type === "pct_revenue" && parsePercentText(rows[id].pctText, PCT_RATE_MESSAGES).error);
    if (badPct.length) {
      setSavedNote(`Not saved — fix the % of Revenue rate on: ${badPct.map(id => (vendors.find(v => v.id === id) || {}).name || id).join(", ")}.`);
      setNoteIsError(true);
      return;
    }
    setSaving(true); setSavedNote(""); setNoteIsError(false);
    try {
      const objs = ids.map(id => buildVendorObj(vendors.find(v => v.id === id), rows[id])).filter(Boolean);
      await onSaveVendors(objs);
      /* The handed-off rows leave the pending batch in BOTH modes — the host now
       * owns them (persist mode: written; local mode: folded into step6Vendors),
       * and their badge flips to "Set" via the live vendors prop either way.
       * ★ C3: dirtyIds clears in both modes too — it means "edits this component
       * still holds", which is now false. What used to be WRONG is that clearing it
       * also silenced the BROWSER guard in local mode; `localApplied` below keeps
       * that armed, so the guard survives the clear instead of depending on it. */
      const savedSet = new Set(ids);
      setConfirmedIds(prev => new Set([...prev].filter(id => !savedSet.has(id))));
      setDirtyIds(prev => new Set([...prev].filter(id => !savedSet.has(id))));
      if (isLocal) setLocalApplied(true);
      /* ★ C3: THE NOTE MUST NOT CLAIM A WRITE THAT DID NOT HAPPEN — and it names the
       * next action, because the control that performs it is on the screen BEHIND
       * this modal and the operator has no way to discover it from here. */
      setSavedNote(isLocal
        ? `${objs.length} vendor${objs.length === 1 ? "" : "s"} updated — not yet saved. Close and click Save Progress to save.`
        : `Saved ${objs.length} vendor${objs.length === 1 ? "" : "s"}.`);
    } catch (e) {
      // ★ Item 8b: a NAMED cause reaches the operator. The unrouted-vendor refuse
      // is raised by saveWizardVendors BEFORE any write (all-or-nothing), and its
      // message lists the offending vendors — "see console" would hide exactly the
      // information needed to fix it.
      setSavedNote(e?.code === ERR_NO_IS_LINE ? e.message : "Save failed — see console.");
      setNoteIsError(true);
      // eslint-disable-next-line no-console
      console.error("Wizard save failed:", e);
    } finally { setSaving(false); }
  };

  const labelFor = (slug) => isLineTaxonomy?.byKey?.get(slug)?.label || "";
  const COLS = ["", "Vendor", "IS Line", "History", "Type", "Frequency", "Start", "End", "Forecast"];

  return (
    <GuardedDialog isDirty={dirty} onClose={onClose} note={null} overlayStyle={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }} cardStyle={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.1)", borderRadius: "20px", padding: "24px", width: "1380px", maxWidth: "96vw", maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}>
        {closeAsk && dirty && (
          <div data-fw-close-ask="" style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", padding: "10px 14px", borderRadius: "8px", background: "#FFF8E1", border: "1px solid #FFE0A0", fontSize: "12px", color: "#8C6500" }}>
            <span style={{ flex: 1 }}>{`You have edits that have not been ${isLocal ? "applied" : "saved"}. Close and discard them?`}</span>
            <button data-fw-close-keep="" onClick={() => setCloseAsk(false)} style={{ padding: "6px 14px", borderRadius: "8px", border: "1px solid rgba(0,0,0,0.12)", background: "#fff", color: "#4B5563", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>Keep editing</button>
            <button data-fw-close-discard="" onClick={() => { setCloseAsk(false); onClose(); }} style={{ padding: "6px 14px", borderRadius: "8px", border: "none", background: "#E07A5F", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: 700 }}>Discard and close</button>
          </div>
        )}
        {/* ── header ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1A1C2E" }}>Set Vendor Forecasts{unsaved && <span style={{ color: "#E67E22", marginLeft: "6px" }}>*</span>}</h3>
            <div style={{ fontSize: "12px", color: "#8B8FA3", marginTop: "2px" }}>{doneCount} of {vendors.length} set{unsaved ? " · unsaved changes" : ""}</div>
          </div>
          <button onClick={requestClose} style={{ padding: "8px 18px", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.12)", background: "transparent", color: "#6B7094", cursor: "pointer", fontSize: "13px", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>Close</button>
        </div>

        {/* ── prepaid-not-here note ── */}
        <div style={{ fontSize: "11px", color: "#6B7094", background: "rgba(46,134,193,0.05)", border: "1px solid rgba(46,134,193,0.12)", borderRadius: "8px", padding: "7px 12px", marginBottom: "12px" }}>
          Showing constant &amp; rule-based vendors. Prepaid vendors are forecast via their contracts on the Prepaid tab and aren't listed here.
        </div>

        {/* ── prefill rule toggle (constant rows only) ── */}
        <ViewOnly canEdit={canEdit}><div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px", padding: "10px 14px", borderRadius: "10px", background: "linear-gradient(135deg,rgba(46,134,193,0.06),rgba(52,152,219,0.06))", border: "1px solid rgba(46,134,193,0.18)" }}>
          <span style={{ fontSize: "11px", fontWeight: 700, color: "#6B7094", textTransform: "uppercase", letterSpacing: "0.5px" }}>Prefill rule</span>
          {RULES.map(r => (
            <button key={r.id} onClick={() => applyRule(r.id)} style={{
              padding: "6px 14px", borderRadius: "8px", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
              border: rule === r.id ? "1px solid #2E86C1" : "1px solid rgba(0,0,0,0.12)",
              background: rule === r.id ? "linear-gradient(135deg,#2E86C1,#3498DB)" : "#fff",
              color: rule === r.id ? "#fff" : "#4A4E69",
            }}>{r.label}</button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: "11px", color: "#8B8FA3", fontStyle: "italic" }}>Prefills constant rows · override any value below</span>
        </div>

        {/* ── confirm controls (bulk check for a full save) ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span style={{ fontSize: "12px", fontWeight: 600, color: "#4A4E69" }}>{confirmedIds.size} checked to {VERB.toLowerCase()}</span>
          {[["Check all", checkAll], ["Check all todo", checkAllTodo], ["Clear", clearChecks]].map(([label, fn]) => (
            <button key={label} onClick={fn} style={{ padding: "5px 12px", borderRadius: "8px", border: "1px solid rgba(46,134,193,0.35)", background: "#fff", fontSize: "11px", fontWeight: 600, color: "#2E86C1", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>{label}</button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: "11px", color: "#8B8FA3", fontStyle: "italic" }}>{isLocal ? "Apply updates checked rows" : "Save writes checked rows"} · any edit auto-checks</span>
        </div>

        {/* ── grid ── */}
        <div style={{ flex: 1, overflowY: "auto", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "12px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                {COLS.map((h, i) => <th key={i} style={{ padding: "10px", textAlign: i === 0 ? "center" : i >= 8 ? "right" : "left", fontSize: "10px", fontWeight: 600, color: "#8B8FA3", textTransform: "uppercase", background: "#FAFBFC", position: "sticky", top: 0, zIndex: 1 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {vendors.map(v => (
                <WizardRow key={v.id} vendor={v} ctx={ctxById[v.id]} row={rows[v.id]} onChange={onRowChange}
                  forecastMonths={forecastMonths} actualsMonths={actualsMonths} isLabel={labelFor(v.isCat)}
                  confirmed={confirmedIds.has(v.id)} onToggleConfirm={toggleConfirm} done={isDone(v, cutoff, hasSourceOptions)}
                  seatDeptOptions={seatDeptOptions} hasSourceOptions={hasSourceOptions} />
              ))}
            </tbody>
          </table>
        </div></ViewOnly>

        {/* ── footer — Save writes CHECKED rows (2b-i); full resume/progress in 2b-ii ── */}
        <div style={{ display: "flex", gap: "12px", marginTop: "16px", justifyContent: "flex-end", alignItems: "center" }}>
          {/* ★ Item 8b: the note is GREEN for success and RED for a failure — a
              refusal rendered in success-green is a lie the operator will read as
              "saved". noteIsError is set by doSave alongside the message. */}
          {/* ★ C3: a LOCAL apply's note is AMBER, not success-green. Green is the
              colour this screen uses for "it is written", and painting a memory-only
              fold in it is the same lie the label was telling. Errors stay red. */}
          <div style={{ flex: 1, fontSize: "11px", color: savedNote ? (noteIsError ? "#C0392B" : (isLocal ? "#E67E22" : "#27AE60")) : unsaved ? "#E67E22" : "#8B8FA3", fontStyle: noteIsError ? "normal" : "italic", fontWeight: noteIsError ? 600 : 400 }}>
            {/* ★ C3 (C): the old idle-dirty line read "click outside won't close." —
                true of the backdrop, false of the Close button beside it. Both now
                take requestClose, so the copy can describe one honest behaviour. */}
            {!canEdit ? <ViewOnlyNote/> : savedNote || (
              unsaved
                ? (isLocal
                    ? "Not yet saved to the database — close, then click Save Progress."
                    : "Unsaved changes — closing will ask before discarding.")
                : (isLocal
                    ? "Set forecasts, check rows, then Apply. Nothing reaches the database until Save Progress."
                    : "Set forecasts, check rows, then Save. Re-openable anytime to adjust."))}
          </div>
          <ViewOnly canEdit={canEdit}><button onClick={doSave} disabled={saving || confirmedIds.size === 0}
            style={{ padding: "10px 28px", borderRadius: "10px", border: "none",
              background: (saving || confirmedIds.size === 0) ? "#CBD0DA" : "linear-gradient(135deg,#27AE60,#2ECC71)",
              color: "#fff", cursor: (saving || confirmedIds.size === 0) ? "not-allowed" : "pointer",
              fontSize: "13px", fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
              boxShadow: (saving || confirmedIds.size === 0) ? "none" : "0 4px 20px rgba(39,174,96,0.3)" }}>
            {/* ★ C3: the VERB is the host's, the COUNT is unchanged (confirmedIds.size —
                it was never the broken half; "{N}" always resolved correctly). */}
            {saving ? `${isLocal ? "Applying" : "Saving"}…` : `${VERB}${confirmedIds.size ? ` ${confirmedIds.size}` : ""}`}
          </button></ViewOnly>
        </div>
    </GuardedDialog>
  );
}
